/**
 * Match Moderation Component
 * View and moderate active game matches, cohorts, and players
 */

import { EmptyState } from '../../components/empty-state';
import { FormMessage } from '../../components/form-field';
import { SearchInput } from '../../components/search-input';
import { Modal } from '../../components/modal';
import { useState } from 'react';
import { Button } from '../../components/button';
import { Pill } from '../../components/pill';
import { useTranslation } from 'react-i18next';
import type { GameWorld, PlayerCohort, MatchPlayer } from '../../api/moderator/moderation/types';
import { useAuthStore } from '../../services/authentication/auth-store';
import { getGameClientUrl } from '../../config/environment';
import { moderatorJoinUrl } from '../../utils/launch-url';
import {
  MATCH_LIST_POLL_SECONDS,
  useBanPlayer,
  useModerationMatches,
  usePlayerDetails,
  useReadPlayerDetails,
  useResolveHelp,
} from '../../api/moderator/moderation/hooks';
import { cohortAsListed, cohortContextOf, profitText, profitTone, type CohortContext, type PlayerPlace, type ProfitTone } from './moderation-view';
import { useNowSeconds } from '../../utils/use-now-seconds';
import { formatMinutesSeconds } from '../../utils/format-date';
import { useVoiceSession } from './use-voice-session';
import { sayActionOutcome } from './action-outcome';
import { PlayerMatchBusyError } from '../../api/moderator/moderation/helpers';
import { VoiceMonitorWindow } from './voice-monitor-window';
import { PrivateVoiceChatWindow } from './private-voice-chat-window';
import { CommonHeader } from '../shell/common-header';
import { showErrorToast, showInfoToast } from '../../components/toast/toast-store';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import moderation from './match-moderation.module.css';
import sharedModal from './shared-modal-styles.module.css';
import { WorldAndTeam } from './world-and-team';
import { nameAsSaid } from './said-names';

/**
 * The two kinds of raised hand. Keyed rather than composed into
 * `help-indicator--${type}`, which a module cannot resolve — the badge would
 * render with no colour at all and nothing would report it.
 */
const HELP_TONE: Record<'individual' | 'cohort', string> = {
  individual: moderation.helpIndicatorIndividual,
  cohort: moderation.helpIndicatorCohort,
};

/** A profit's tone (profitTone), by its class: keyed, so a tone with no class is a build error. */
const PROFIT_TONE: Record<ProfitTone, string> = {
  positive: moderation.profitPositive,
  negative: moderation.profitNegative,
  unavailable: moderation.profitUnavailable,
};

/**
 * A session script's steps by their ids, named: keys written whole. A script
 * names its own steps, so a step not here shows as its id.
 */
const SCRIPT_STEP_KEY: Partial<Record<string, string>> = {
  intro: 'matchModeration.sessionScriptStep.intro',
  hq_selection: 'matchModeration.sessionScriptStep.hq_selection',
  debrief: 'matchModeration.sessionScriptStep.debrief',
};

/**
 * A team activity's phases by their types (the server's phase-*.go), named:
 * keys written whole. A phase the portal does not know yet shows as its type.
 */
const SCRIPT_PHASE_KEY: Partial<Record<string, string>> = {
  selection: 'matchModeration.sessionScriptPhase.selection',
  voting: 'matchModeration.sessionScriptPhase.voting',
  quiz: 'matchModeration.sessionScriptPhase.quiz',
  speaking_rounds: 'matchModeration.sessionScriptPhase.speaking_rounds',
  synced_dialogue: 'matchModeration.sessionScriptPhase.synced_dialogue',
  per_player_synced_dialogue: 'matchModeration.sessionScriptPhase.per_player_synced_dialogue',
  results: 'matchModeration.sessionScriptPhase.results',
  earn_achievement: 'matchModeration.sessionScriptPhase.earn_achievement',
};

interface MatchModerationProps {
  onBack: () => void;
}

/**
 * The open player card's player, and where the list showed them as it opened
 * (PlayerPlace): their details, the match they are in among them, are read
 * through the query cache (usePlayerDetails).
 */
interface PlayerRef extends PlayerPlace {
  customId: string;
}

export function MatchModeration({ onBack }: MatchModerationProps) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  // The match list: read now, again every MATCH_LIST_POLL_SECONDS, on Refresh
  // and after a ban or a resolved hand. A failed read keeps what was last read,
  // and the screen says it failed.
  const matches = useModerationMatches();
  const games = matches.data ?? [];
  const isInitialLoading = matches.isPending;
  const isRefreshing = matches.isFetching && !matches.isPending;
  const loadFailed = matches.isError;
  // The open cards draw from the cache, so a poll or a moderator's action
  // shows on the open card: the cohort card holds its team as it last drew it
  // (cohortAsListed), the player card who its player is.
  const [openedCohort, setOpenedCohort] = useState<CohortContext | null>(null);
  const [playerRef, setPlayerRef] = useState<PlayerRef | null>(null);
  const selectedCohortContext = openedCohort ? cohortAsListed(games, openedCohort) : null;
  // The card holds what it draws: the team as listed, once that changed; the
  // same team while its match is listed unreadable; and nothing once its match
  // or its team is no longer listed — it closes, and stays closed, where left
  // open on nothing it opened again by itself once the team was listed again
  // (a team is listed while a player of it is online).
  if (selectedCohortContext !== openedCohort) setOpenedCohort(selectedCohortContext);
  const playerDetails = usePlayerDetails(playerRef?.customId ?? null);
  const selectedPlayer = playerRef ? (playerDetails.data ?? null) : null;
  const selectedPlayerContext = playerRef;
  const [searchFilter, setSearchFilter] = useState('');
  const [collapsedWorlds, setCollapsedWorlds] = useState<Set<string>>(new Set());
  
  // The moderator can have a voice monitor over the cohort or a private chat
  // with one player, never both. That interlock and its five pieces of state
  // live in the hook.
  const {
    showVoiceMonitor,
    voiceMonitorConnection,
    isConnectingVoice,
    showPrivateVoiceChat,
    isStartingPrivateChat,
    openVoiceMonitor: handleOpenVoiceMonitor,
    closeVoiceMonitor: handleCloseVoiceMonitor,
    startPrivateChat: handleStartPrivateChat,
    closePrivateChat: handleClosePrivateChat,
  } = useVoiceSession({ selectedCohortContext, selectedPlayer, selectedPlayerContext });
  const resolveHelp = useResolveHelp();
  const banPlayer = useBanPlayer();
  const readPlayerDetails = useReadPlayerDetails();

  // A player's name, a moderator's marked so (nameAsSaid).
  const getPlayerDisplayName = (player: MatchPlayer): string => nameAsSaid(t, player.name, player.role === 'moderator');

  const handleRefresh = () => {
    void matches.refetch();
  };

  // A cohort's card opens on the cohort as the list has it, and follows it.
  const handleCohortClick = (cohort: PlayerCohort, gameId: string, world: GameWorld) => {
    setOpenedCohort(cohortContextOf(gameId, world, cohort));
    setPlayerRef(null);
  };

  // A player's card opens once their details are read: a read that fails is
  // said, and opens no card.
  const handlePlayerClick = async (player: MatchPlayer, gameId: string, worldName: string, cohort: PlayerCohort) => {
    // Use customId if available, otherwise fall back to id
    const lookupId = player.customId || player.id;
    try {
      await readPlayerDetails(lookupId);
    } catch (error) {
      console.error('[MatchModeration] Failed to read the player:', error);
      // The same failure, said as what it is when the player's match was too busy to read.
      showErrorToast(t(error instanceof PlayerMatchBusyError ? 'matchModeration.playerMatchBusy' : 'matchModeration.detailsLoadError'));
      return;
    }
    setPlayerRef({ customId: lookupId, gameId, worldName, cohortId: cohort.id, cohortName: cohort.name });
    setOpenedCohort(null);
  };

  const handleJoinGame = () => {
    if (!selectedCohortContext || !currentUser) {
      console.error('[MatchModeration] Cannot join game: missing context or user');
      return;
    }

    if (!currentUser.nakamaCustomId) {
      console.error('[MatchModeration] Cannot join game: currentUser.nakamaCustomId is missing. Re-login may be required.');
      showErrorToast(t('matchModeration.missingIdentityTitle'), t('matchModeration.missingIdentityMessage'));
      return;
    }

    // Check if Voice Monitor is open - must close it first to avoid voice channel conflicts
    if (showVoiceMonitor) {
      showInfoToast(t('matchModeration.closeVoiceMonitorFirst'));
      return;
    }

    const { cohort, worldId, matchId } = selectedCohortContext;

    // The game for this environment (local, develop, staging, production), in
    // a new tab, in the language the moderator reads the portal in.
    const joinUrl = moderatorJoinUrl(getGameClientUrl(), {
      user: currentUser.nakamaCustomId,
      name: currentUser.name,
      cohort: cohort.id,
      world: worldId,
      match: matchId,
    });
    window.open(joinUrl, '_blank');
  };

  const handleResolveHelp = async () => {
    // No `customId` guard here, unlike start-chat and ban: this one resolves by
    // `selectedPlayer.id`. Requiring customId would silently refuse to clear a
    // help request for any player that has none.
    if (!selectedPlayer || !currentUser) {
      console.error('[MatchModeration] Cannot resolve help: missing context or user');
      return;
    }

    // The player's match, as their details read it — where this searched the
    // list's last poll for the player, and found none in a match that poll
    // had found too busy to read, so the action went nowhere, silently.
    const matchId = selectedPlayer.currentMatchId;
    if (!matchId) {
      showErrorToast(t('matchModeration.resolveHelpError'), t('matchModeration.playerNotInMatch'));
      return;
    }

    try {
      console.log('[MatchModeration] Resolving help request for:', selectedPlayer.id);

      // Call portal-backend to resolve help request. The card shows the hand
      // lowered once the player is read again (useResolveHelp reads the
      // moderator's data again); a busy match lowers it once free, said so.
      const response = await resolveHelp.mutateAsync({ userId: selectedPlayer.id, matchId });
      sayActionOutcome(t, response, { queued: 'matchModeration.resolveHelpQueued', failed: 'matchModeration.resolveHelpError' });
    } catch (error) {
      console.error('[MatchModeration] Failed to resolve help request:', error);
      showErrorToast(t('matchModeration.resolveHelpError'), error instanceof Error ? error.message : undefined);
    }
  };

  const handleBanPlayer = async () => {
    if (!selectedPlayer?.customId || !currentUser) {
      console.error('[MatchModeration] Cannot ban player: missing context or user');
      return;
    }

    // The player's match, as their details read it (handleResolveHelp).
    const matchId = selectedPlayer.currentMatchId;
    if (!matchId) {
      showErrorToast(t('matchModeration.banPlayerError'), t('matchModeration.playerNotInMatch'));
      return;
    }

    // Confirmation dialog
    const confirmed = await confirmDialog({
      message: t('matchModeration.banPlayerConfirm'),
      confirmLabel: t('matchModeration.banPlayer'),
      tone: 'danger',
    });
    if (!confirmed) {
      return;
    }

    try {
      console.log('[MatchModeration] Banning player:', selectedPlayer.customId);

      // The list reads again once the ban answers (useBanPlayer). A busy
      // match bans the player once it is free: said so, not as done; and the
      // card closes on a ban done or on its way.
      const response = await banPlayer.mutateAsync({
        playerCustomId: selectedPlayer.customId,
        matchId,
        reason: 'Banned by moderator',
      });
      const outcome = sayActionOutcome(t, response, {
        done: 'matchModeration.banPlayerSuccess',
        queued: 'matchModeration.banPlayerQueued',
        failed: 'matchModeration.banPlayerError',
      });
      if (outcome !== 'failed') setPlayerRef(null);
    } catch (error) {
      console.error('[MatchModeration] Failed to ban player:', error);
      showErrorToast(t('matchModeration.banPlayerError'), error instanceof Error ? error.message : undefined);
    }
  };

  const closeModal = () => {
    setOpenedCohort(null);
    setPlayerRef(null);
  };

  const toggleWorldCollapse = (worldId: string) => {
    setCollapsedWorlds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(worldId)) {
        newSet.delete(worldId);
      } else {
        newSet.add(worldId);
      }
      return newSet;
    });
  };

  const collapseAll = () => {
    const allWorldIds = games.flatMap((game) => game.worlds.map((world) => world.id));
    setCollapsedWorlds(new Set(allWorldIds));
  };

  const expandAll = () => {
    setCollapsedWorlds(new Set());
  };

  // Filter games and worlds based on search
  const filteredGames = games.map((game) => ({
    ...game,
    worlds: game.worlds.filter((world) => {
      const searchLower = searchFilter.toLowerCase();
      const gameName = game.name.toLowerCase();
      const worldName = world.name.toLowerCase();
      return gameName.includes(searchLower) || worldName.includes(searchLower);
    }),
  })).filter((game) => game.worlds.length > 0);

  const hasResults = filteredGames.length > 0;

  return (
    <div className={`portal-shell ${moderation.matchModerationContainer}`}>
      {/* Header */}
      <CommonHeader 
        title={t('matchModeration.title')} 
        showBackButton 
        onBackClick={onBack}
      />

      {/* Main Content */}
      <main className={moderation.matchModerationMain}>
        {isInitialLoading ? (
          <div className={moderation.loadingMessage}>{t('common.loading')}</div>
        ) : (
          <div className={moderation.gamesContainer}>
            {/* Filter Bar */}
            <div className={moderation.filterBar}>
              <SearchInput
                grow
                placeholder={t('matchModeration.filterPlaceholder')}
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                onClear={() => setSearchFilter('')}
                clearLabel={t('common.clearSearch')}
              />
              <div className={moderation.collapseButtons}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  title={t('matchModeration.autoRefreshInfo', { seconds: MATCH_LIST_POLL_SECONDS })}
                >
                  {isRefreshing ? '⏳' : '🔄'} {t('matchModeration.refresh')}
                </Button>
                <Button variant="ghost" size="sm" onClick={expandAll}>
                  {t('matchModeration.expandAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAll}>
                  {t('matchModeration.collapseAll')}
                </Button>
              </div>
            </div>

            {/* Auto-refresh info with refresh indicator */}
            <div className={`${moderation.mmAutorefreshNote} tone-soft`}>
              {isRefreshing ? (
                <span className="tone-ok">⟳ {t('matchModeration.refreshing')}...</span>
              ) : (
                t('matchModeration.autoRefreshInfo', { seconds: MATCH_LIST_POLL_SECONDS })
              )}
            </div>

            {loadFailed && <FormMessage>{t('matchModeration.loadError', { seconds: MATCH_LIST_POLL_SECONDS })}</FormMessage>}

            {/* Results — none found is only said of a list that was read */}
            {!hasResults ? (
              loadFailed ? null : <EmptyState surface>{t('matchModeration.noResults')}</EmptyState>
            ) : (
              filteredGames.map((game) => (
                <div key={game.id} className={moderation.gameSection}>
                  {game.worlds.map((world) => {
                    const isCollapsed = collapsedWorlds.has(world.id);
                    return (
                      <div key={world.id} className={moderation.worldSection}>
                        <div className={moderation.worldHeader}>
                          <h2 className={moderation.worldTitle}>
                            {game.name} - {world.name}
                          </h2>
                          <button
                            className={moderation.collapseToggle}
                            onClick={() => toggleWorldCollapse(world.id)}
                            type="button"
                          >
                            {isCollapsed ? '▶' : '▼'}
                          </button>
                        </div>
                        
                        {!isCollapsed && world.detailsUnreadable && (
                          // Running, and listed so, but the game server could
                          // not read its cohorts or its session's details.
                          <EmptyState inline>{t('matchModeration.detailsUnreadable')}</EmptyState>
                        )}
                        {!isCollapsed && !world.detailsUnreadable && (
                          <>
                            <div className={moderation.cohortsGrid}>
                              {world.cohorts.map((cohort) => {
                                const hasHelpRequests = cohort.players.some(p => p.helpRequestType);
                                return (
                                  <div
                                    key={cohort.id}
                                    className={`${moderation.cohortCard} ${hasHelpRequests ? moderation.cohortCardHasHelp : ''}`}
                                    onClick={() => handleCohortClick(cohort, game.id, world)}
                                  >
                                    <div className={moderation.cohortHeader}>
                                      <h3 className={moderation.cohortName}>
                                        {hasHelpRequests && <span className={moderation.helpBadge}>🆘</span>}
                                        {cohort.name}
                                      </h3>
                                      <div className={moderation.cohortSessionScriptStatus}>
                                        {(() => {
                                          const stepIds = world.sessionScriptStepIds;
                                          const stepIndex = world.sessionScriptStepIndex ?? 0;
                                          const stepState = world.sessionScriptStepState;

                                          if (!world.sessionScriptId || !stepIds?.length) {
                                            return (
                                              <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeNormal}`}>
                                                {t('matchModeration.sessionScriptPhase.normalPlay')}
                                              </span>
                                            );
                                          }

                                          const stepName = (idx: number) => {
                                            const key = SCRIPT_STEP_KEY[stepIds[idx]];
                                            return key ? t(key) : stepIds[idx];
                                          };
                                          const phaseType = cohort.sessionScriptPhaseType;
                                          const phaseKey = phaseType ? SCRIPT_PHASE_KEY[phaseType] : undefined;

                                          // All steps finished
                                          if (stepState === 'completed') {
                                            return (
                                              <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeStepCompleted}`}>
                                                {stepIds.length} {stepName(stepIds.length - 1)} — {t('matchModeration.sessionScriptStep.stepState.allComplete')}
                                              </span>
                                            );
                                          }

                                          // Step is actively running
                                          if (stepState === 'active') {
                                            return (
                                              <>
                                                <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeStep} ${moderation.sessionScriptBadgeStepActive}`}>
                                                  {stepIndex + 1} {stepName(stepIndex)} ({t('matchModeration.sessionScriptStep.stepState.running')})
                                                </span>
                                                {cohort.sessionScriptActivityId && !cohort.sessionScriptActivityDone ? (
                                                  <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeActive}`}>
                                                    {phaseKey ? t(phaseKey) : phaseType ?? ''}
                                                    {cohort.sessionScriptPhaseCount != null && cohort.sessionScriptPhaseCount > 1 && (
                                                      <> ({(cohort.sessionScriptPhaseIndex ?? 0) + 1}/{cohort.sessionScriptPhaseCount})</>
                                                    )}
                                                  </span>
                                                ) : cohort.sessionScriptActivityDone ? (
                                                  <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeDone}`}>
                                                    {t('matchModeration.sessionScriptPhase.activityDone')}
                                                  </span>
                                                ) : null}
                                              </>
                                            );
                                          }

                                          // pending: either before start (index 0) or between steps (index > 0)
                                          if (stepIndex === 0) {
                                            return (
                                              <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeStep} ${moderation.sessionScriptBadgeStepPending}`}>
                                                1 {stepName(0)} ({t('matchModeration.sessionScriptStep.stepState.waiting')})
                                              </span>
                                            );
                                          }

                                          // Between steps: previous ended, current waiting
                                          return (
                                            <>
                                              <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeStep} ${moderation.sessionScriptBadgeStepCompleted}`}>
                                                {stepIndex} {stepName(stepIndex - 1)} ({t('matchModeration.sessionScriptStep.stepState.ended')})
                                              </span>
                                              <span className={`${moderation.sessionScriptBadge} ${moderation.sessionScriptBadgeStep} ${moderation.sessionScriptBadgeStepPending}`}>
                                                {stepIndex + 1} {stepName(stepIndex)} ({t('matchModeration.sessionScriptStep.stepState.waiting')})
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                      <div className={`${moderation.cohortProfit} ${PROFIT_TONE[profitTone(cohort.lastTickProfit)]}`}>
                                        {t('matchModeration.profit')}: {profitText(cohort.lastTickProfit)}
                                      </div>
                                    </div>
                                  <ul className={moderation.playerList}>
                                    {[...cohort.players]
                                      .sort((a, b) => {
                                        // Players with help requests come first
                                        const aHasHelp = a.helpRequestType ? 1 : 0;
                                        const bHasHelp = b.helpRequestType ? 1 : 0;
                                        return bHasHelp - aHasHelp;
                                      })
                                      .map((player) => (
                                        <li
                                          key={`${cohort.id}-${player.id}`}
                                          className={`${moderation.playerItem} ${
                                            player.helpRequestType === 'individual' 
                                              ? moderation.playerItemHelpIndividual
                                              : player.helpRequestType === 'cohort'
                                              ? moderation.playerItemHelpCohort
                                              : player.isInPrivateChat
                                              ? moderation.playerItemPrivateChat
                                              : ''
                                          }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handlePlayerClick(player, game.id, world.name, cohort);
                                          }}
                                        >
                                          <span className={moderation.playerName}>
                                            <span>{getPlayerDisplayName(player)}</span>
                                            {player.isInPrivateChat && (
                                              <span className={`${moderation.helpIndicator} ${moderation.helpIndicatorPrivateChat}`}>
                                                💬 {t('matchModeration.privateChat')}
                                              </span>
                                            )}
                                            {player.helpRequestType && (
                                              <span className={`${moderation.helpIndicator} ${HELP_TONE[player.helpRequestType]}`}>
                                                🆘 {player.helpRequestType === 'individual' ? t('matchModeration.individualHelp') : t('matchModeration.cohortHelp')}
                                              </span>
                                            )}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <TimeInMatch sessionStartTime={world.sessionStartTime} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Cohort Modal */}
      {selectedCohortContext && (
        <Modal
          isOpen
          onClose={closeModal}
          size="lg"
          title={`${t('matchModeration.cohort')}: ${selectedCohortContext.cohort.name}`}
          footer={
            <>
              <Button onClick={handleOpenVoiceMonitor} disabled={isConnectingVoice} type="button">
                🎧📝 {isConnectingVoice ? t('voiceRoom.connecting') : t('voiceMonitor.openButton')}
              </Button>
              <Button onClick={handleJoinGame} variant="ghost" type="button">
                🎮 {t('matchModeration.joinGame')}
              </Button>
            </>
          }
        >

            <WorldAndTeam world={selectedCohortContext.worldId} team={selectedCohortContext.cohort.name} size="boxed" />

            {/* Content */}
            <div className={sharedModal.modalSection}>
              <h3 className={sharedModal.modalSectionTitle}>{t('matchModeration.details')}</h3>
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>ID:</span>
                <span className={sharedModal.modalInfoRowValue}>{selectedCohortContext.cohort.id}</span>
              </div>
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>{t('matchModeration.players')}:</span>
                <span className={sharedModal.modalInfoRowValue}>{selectedCohortContext.cohort.players.length}</span>
              </div>
              
              {/* Last Tick Profit */}
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>{t('matchModeration.lastTickProfit')}:</span>
                <span className={`${sharedModal.modalInfoRowValue} ${moderation.cardProfit} ${PROFIT_TONE[profitTone(selectedCohortContext.cohort.lastTickProfit)]}`}>
                  {profitText(selectedCohortContext.cohort.lastTickProfit)}
                </span>
              </div>

              {/* Last Quarter Profit */}
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>{t('matchModeration.lastQuarterProfit')}:</span>
                <span className={`${sharedModal.modalInfoRowValue} ${moderation.cardProfit} ${PROFIT_TONE[profitTone(selectedCohortContext.cohort.lastQuarterProfit)]}`}>
                  {profitText(selectedCohortContext.cohort.lastQuarterProfit)}
                </span>
              </div>
            </div>

            <div className={sharedModal.modalSection}>
              <h3 className={sharedModal.modalSectionTitle}>{t('matchModeration.players')}</h3>
              <div className={sharedModal.modalList}>
                {selectedCohortContext.cohort.players.map((player) => (
                  <div key={`${selectedCohortContext.cohort.id}-${player.id}`} className={sharedModal.modalListItem}>
                    {getPlayerDisplayName(player)}
                  </div>
                ))}
              </div>
            </div>
        </Modal>
      )}

      {/* Player Modal */}
      {selectedPlayer && (
        <Modal
          isOpen
          onClose={closeModal}
          size="lg"
          title={`${t('matchModeration.player')}: ${getPlayerDisplayName(selectedPlayer)}`}
          footer={
            <>
              {selectedPlayer.helpRequestType && (
                <Button onClick={handleResolveHelp} disabled={resolveHelp.isPending} variant="ghost" type="button">
                  ✅ {t('matchModeration.resolveHelp')}
                </Button>
              )}
              <Button onClick={handleStartPrivateChat} disabled={isStartingPrivateChat} type="button">
                💬 {isStartingPrivateChat ? t('privateVoiceChat.connecting', { playerName: selectedPlayer.name }) : t('privateVoiceChat.startButton')}
              </Button>
              <Button onClick={handleBanPlayer} disabled={banPlayer.isPending} variant="dangerQuiet" type="button">
                🚫 {t('matchModeration.banPlayer')}
              </Button>
            </>
          }
        >

            {selectedPlayerContext && (
              <WorldAndTeam world={selectedPlayerContext.worldName} team={selectedPlayerContext.cohortName} size="boxed" />
            )}

            {/* Content */}
            <div className={sharedModal.modalSection}>
              <h3 className={sharedModal.modalSectionTitle}>{t('matchModeration.details')}</h3>
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>ID:</span>
                <span className={sharedModal.modalInfoRowValue}>{selectedPlayer.id}</span>
              </div>
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>Custom ID:</span>
                <span className={sharedModal.modalInfoRowValue}>{selectedPlayer.customId}</span>
              </div>
              <div className={sharedModal.modalInfoRow}>
                <span className={sharedModal.modalInfoRowLabel}>{t('matchModeration.player')}:</span>
                <span className={sharedModal.modalInfoRowValue}>{getPlayerDisplayName(selectedPlayer)}</span>
              </div>
            </div>

            {/* Status Badges */}
            {(selectedPlayer.isInPrivateChat || (selectedPlayer.helpRequestType)) && (
              <div className={sharedModal.modalSection}>
                <h3 className={sharedModal.modalSectionTitle}>Status</h3>
                
                {/* Private Chat Status */}
                {selectedPlayer.isInPrivateChat && (
                  <Pill tone="info" className={moderation.mmStatusPill}>
                    💬 {t('matchModeration.inPrivateChat')}
                  </Pill>
                )}

                {/* Help Request Status */}
                {selectedPlayer.helpRequestType && (
                  <Pill tone={selectedPlayer.helpRequestType === 'individual' ? 'info' : 'warning'}>
                    🆘 {t('matchModeration.helpRequestActive')} - {selectedPlayer.helpRequestType === 'individual' ? t('matchModeration.individualHelp') : t('matchModeration.cohortHelp')}
                  </Pill>
                )}
              </div>
            )}
        </Modal>
      )}

      {/* Voice Monitor Window (integrated voice + transcription) */}
      {showVoiceMonitor && voiceMonitorConnection && (
        <VoiceMonitorWindow
          token={voiceMonitorConnection.token}
          serverUrl={voiceMonitorConnection.serverUrl}
          worldName={voiceMonitorConnection.worldName}
          cohortName={voiceMonitorConnection.cohortName}
          onClose={handleCloseVoiceMonitor}
        />
      )}

      {/* Private Voice Chat Window */}
      {showPrivateVoiceChat && <PrivateVoiceChatWindow onClose={handleClosePrivateChat} />}
    </div>
  );
}

/**
 * How long a match has run, counted every second in its own cell, on the
 * clock every cell shares (useNowSeconds, mirrored with the game client's).
 * The clock was the screen's, and drew it all again each second — every match
 * and card, and both call windows with their rooms' children — for one line
 * of text.
 */
function TimeInMatch({ sessionStartTime }: { sessionStartTime: number }) {
  const { t } = useTranslation();
  const now = useNowSeconds();
  return (
    <div className={moderation.timeInMatch}>
      {t('matchModeration.timeInMatch')}: {formatMinutesSeconds(now - sessionStartTime)}
    </div>
  );
}
